import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
  Observable,
  ReplaySubject,
  from as observableFrom,
  of as observableOf
} from "rxjs";
import {
  catchError,
  distinctUntilChanged,
  filter,
  first,
  map,
  mapTo,
  mergeMap,
  mergeMapTo,
  tap
} from "rxjs/operators";

import { MART_AUTH_UNAUTHORIZED_MESSAGE } from "@bridge/models/user-management";

import { RuntimeConfigService } from "@lantern/app/core/utils";
import { MartJwt } from "@lantern/app/models";

import { MartAuthApi } from "../api/base";
import { AppConfig } from "../app.config";
import { AuthJwtService } from "./auth-jwt.service";
import { LOCATION } from "./constants";

const twoHours = 2 * 60 * 60 * 1000;
export const EXPIRATION_RENEWAL_WINDOW = twoHours;

@Injectable()
export class AuthService {
  onInitialized = new ReplaySubject<boolean>(1);
  isLoggedIn = new ReplaySubject<boolean>(1);

  martJwt: MartJwt | undefined;
  applications: MartJwt["applications"] = {};

  constructor(
    private app: AppConfig,
    protected authJwt: AuthJwtService,
    private martAuthApi: MartAuthApi,
    private runtimeConfig: RuntimeConfigService,
    private router: Router,
    @Inject(LOCATION) private location: Location
  ) {
    this.init();
  }

  init(): void {
    // when initialized, check if mart jwt token exists and set isLoggedIn state
    this.onInitialized
      .pipe(
        filter((initialized) => initialized),
        mergeMap(() =>
          this.app.isGlobalApp
            ? this.authJwt.globalJwtToken
            : this.authJwt.martJwtToken
        ),
        distinctUntilChanged(),
        map((token) => token != null)
      )
      .subscribe(this.isLoggedIn);

    this.authJwt.martJwt.subscribe((martJwt) => {
      this.martJwt = martJwt;
      this.applications = martJwt != null ? martJwt.applications : {};
    });

    // get first mart jwt, if exists check is valid
    // then get the mart jwt
    this.authJwt.martJwtToken
      .pipe(
        first(),
        mergeMap((token) => {
          if (token == null) {
            return observableOf(undefined);
          }
          return this.martAuthApi.verifyToken(token).pipe(
            map((result) => {
              return result.valid ? token : undefined;
            }),
            catchError(() => observableOf(undefined))
          );
        }),
        mergeMap((token) => {
          // if token doesn't exist, try to acquire a new token
          if (token == null && !this.app.isGlobalApp) {
            return this.acquireMartJwt();
          }
          return observableOf(token);
        })
      )
      .subscribe(() => {
        this.onInitialized.next(true);
      });
  }

  login(): Observable<void> {
    if (!this.app.auth.martAuth) {
      throw new Error("Mart auth disabled");
    }

    return this.getMartJwt().pipe(
      map((jwt) => {
        if (jwt == null) {
          throw new Error("No global JWT found");
        }
      })
    );
  }

  logout(): Observable<void> {
    return this.isLoggedIn.pipe(
      mergeMap((isLoggedIn) => {
        return isLoggedIn ? this.authJwt.martJwtToken : observableOf(undefined);
      }),
      first(),
      mergeMap((token) => {
        return token != null
          ? this.martAuthApi.logout(token).pipe(mapTo(undefined))
          : observableOf(undefined);
      }),
      tap(() => {
        this.authJwt.martJwtToken.next(undefined);
        this.goToGlobalLogout();
      })
    );
  }

  getMartJwt(): Observable<string | undefined> {
    return this.onInitialized.pipe(
      mergeMapTo(this.authJwt.martJwt),
      first(),
      mergeMap((martJwt) => {
        if (martJwt != null) {
          const expiresAt = martJwt.exp * 1000,
            shouldRenew = expiresAt - Date.now() <= EXPIRATION_RENEWAL_WINDOW;
          if (!shouldRenew) {
            return this.authJwt.martJwtToken.pipe(first());
          }
        }
        return this.acquireMartJwt();
      })
    );
  }

  private acquireMartJwt(): Observable<string | undefined> {
    return this.authJwt.globalJwtToken.pipe(
      first(),
      mergeMap((globalToken) => {
        if (globalToken == null) {
          return observableOf(undefined);
        }

        return this.martAuthApi.login(globalToken).pipe(
          map((res) => res.jwt),
          catchError((res) => {
            if (res.status === 401) {
              return observableFrom(
                res.error.message === MART_AUTH_UNAUTHORIZED_MESSAGE
                  ? this.goToGlobalLoginWithFromMart()
                  : this.goToGlobalUrl()
              ).pipe(
                map(() => {
                  throw new Error("User does not have access to this mart");
                })
              );
            }
            return observableOf(undefined);
          })
        );
      }),
      tap((martToken) => {
        this.authJwt.martJwtToken.next(martToken);
      })
    );
  }

  hasApplication(appName: string): boolean {
    if (!this.app.auth.martAuth) {
      return true;
    }

    return this.applications[appName] != null;
  }

  hasFeature(featureName: string, appName: string = this.app.name): boolean {
    if (!this.app.auth.martAuth) {
      return true;
    }

    const app = this.applications[appName];
    return app != null && Array.isArray(app) && app.includes(featureName);
  }

  hasPermission(permission: string | undefined): boolean {
    if (!this.app.auth.martAuth) {
      return true;
    }

    if (permission === "true" || permission == null) {
      return this.martJwt != null;
    }
    if (permission === "false") {
      return this.martJwt == null;
    }

    // need to split at least 3 to recognize 3 is a bad case
    let tokens: string[] = permission
      .replace(/^self/, this.app.name)
      .split(".", 3);
    switch (tokens.length) {
      case 1:
        return this.hasApplication(tokens[0]);
      case 2:
        return this.hasFeature(tokens[1], tokens[0]);
      default:
        return false;
    }
  }

  isAdmin(): boolean {
    return this.hasFeature("ADMIN");
  }

  private async goToGlobalLoginWithFromMart(): Promise<void> {
    const currentUrl = encodeURIComponent(location.host);
    await this.goToGlobalUrl("/#/marts?from-mart=" + currentUrl);
  }

  private goToGlobalLogout(): void {
    if (this.app.isGlobalApp) {
      this.router.navigateByUrl("/logout");
    } else {
      this.goToGlobalUrl("/#/logout");
    }
  }

  private async goToGlobalUrl(path: string = ""): Promise<void> {
    const { globalAuthUrl } = await this.runtimeConfig.value;
    this.goToUrl(`${globalAuthUrl}${path}`);
  }

  private goToUrl(url: string): void {
    this.location.href = url;
  }
}
